import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoicesDataViewContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import toggleFiltersOpen from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/ToggleFiltersOpen";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Verndale Custom Feature - Add redux state */
const mapStateToProps = (state: ApplicationState) => ({
    filtersOpen: state.pages.invoiceHistory.filtersOpen,
});

const mapDispatchToProps = {
    toggleFiltersOpen,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceHistoryHeaderStyles {
    container?: GridContainerProps;
    heading?: TypographyProps;
    invoiceCountGridItem?: GridItemProps;
    emptyGridItem?: GridItemProps;
    toggleFilterGridItem?: GridItemProps;
    /** @deprecated Use toggleFilterButtonIcon instead. */
    toggleFilterIcon?: IconProps;
    toggleFilterButton?: ButtonPresentationProps;
    toggleFilterButtonIcon?: ButtonIconPresentationProps;
}

export const headerStyles: InvoiceHistoryHeaderStyles = {
    container: {
        gap: 8,
        css: css`
            padding-bottom: 10px;
        `,
    },
    heading: { variant: "h2", as: "h1" },
    invoiceCountGridItem: {
        width: 11,
        style: { marginTop: "8px", fontWeight: 600 },
    },
    toggleFilterGridItem: {
        width: 1,
        style: { marginTop: "8px", justifyContent: "flex-end" },
    },
    toggleFilterButton: {
        color: "common.background",
        shape: "pill",
        buttonType: "solid",
        shadow: false,
        css: css`
            width: 40px;
            padding: 0;
        `,
    },
    toggleFilterButtonIcon: {
        src: Filter,
        size: 24,
    },
};

const styles = headerStyles;

const InvoiceHistoryHeader = (props: Props) => {
    const invoicesDataView = useContext(InvoicesDataViewContext);
    const invoicesCount =
        invoicesDataView.value && invoicesDataView.pagination ? invoicesDataView.pagination.totalItemCount : 0;

    return (
        <>
            <Typography {...styles.heading} data-test-selector="PageTitle">
                {translate("Invoice History")}
            </Typography>
            <Zone contentId={props.id} zoneName="Content00" />
            <GridContainer {...styles.container}>
                {/* Verndale Custom Feature - Change translation */}
                <GridItem {...styles.invoiceCountGridItem}>{translate("{0} Documents", `${invoicesCount}`)}</GridItem>
                <GridItem {...styles.toggleFilterGridItem}>
                    <Button
                        {...styles.toggleFilterButton}
                        onClick={props.toggleFiltersOpen}
                        data-test-selector="invoiceHistory_toggleFilter"
                    >
                        {/* Verndale Custom Feature - Show label instead of icon */}
                        {props.filtersOpen ? translate("Hide Filters") : translate("Show Filters")}
                    </Button>
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceHistoryHeader as any) as any,
    definition: {
        group: "Invoice History",
        displayName: "Page Header",
        allowedContexts: ["InvoiceHistoryPage"],
    },
};

export default widgetModule;
